'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useRouter } from 'next/navigation';

import Box from '@mui/material/Box';

import { type DisplayConceptRef, SCHEMA_PREFIX_SEARCH_PATH } from '@/lib/api';

import { ControlledConceptAutocomplete } from './ControlledConceptAutocomplete';

/** Configures the navigation-oriented topic search wrapper. */
interface TopicSelectorProps {
  /** Disambiguates the generated autocomplete identifier from sibling selectors. */
  instanceId: string;
  /** Receives a selected topic instead of using the default guide navigation. */
  onChangeFn?: (value: DisplayConceptRef) => void;
  /** Selects the concept-search endpoint used by the wrapped autocomplete. */
  searchPath?: string;
}

/** Searches topics, handles selection navigation, and owns the global slash shortcut. */
export const TopicSelector: React.FC<TopicSelectorProps> = ({
  instanceId,
  onChangeFn,
  searchPath = SCHEMA_PREFIX_SEARCH_PATH,
}) => {
  const router = useRouter();
  const [resetSequence, setResetSequence] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Global "/" keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === '/' && !isUserTyping()) {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };

    const isUserTyping = () => {
      const activeElement = document.activeElement;
      return (
        activeElement instanceof HTMLInputElement || activeElement instanceof HTMLTextAreaElement
      );
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  /** Delegates the selected topic, then resets the autocomplete input. */
  const handleChange = useCallback(
    (newValue: DisplayConceptRef | null): void => {
      if (newValue) {
        if (onChangeFn) {
          onChangeFn(newValue);
        } else {
          router.push(`/guide/${encodeURIComponent(newValue.cref)}`);
        }
        setResetSequence((current) => current + 1);
      }
    },
    [onChangeFn, router]
  );

  return (
    <Box
      sx={{
        width: '90%',
        maxWidth: 500,
        margin: '0.8em',
      }}
    >
      <ControlledConceptAutocomplete
        ariaLabel="Search topics"
        inputRef={inputRef}
        instanceId={`topic-selector-${instanceId}`}
        key={resetSequence}
        onChange={handleChange}
        placeholder="Search topics..."
        searchPath={searchPath}
        value={null}
      />
    </Box>
  );
};
